import Header from "./header";
import Footer from "./footer";
import useDocumentTitle from "./useDocumentTitle";

/**
 * Returns the Privacy Policy page component.
 *
 * @returns The Privacy Policy page component.
 */
const PrivacyPolicy = () => {
  useDocumentTitle("Privacy Policy");
  return (
    <>
      <Header />

      <div className="relative py-16 px-4 sm:px-16 lg:px-48 bg-white overflow-hidden dark:bg-gray-900">
        <div className="bg-white border-t-4 border-blue-600 mx-auto max-w-5xl shadow-md dark:bg-gray-800">
          <div className="px-8 py-8">
            <h2 className=" text-gray-500 text-sm dark:text-gray-400">
              Arctic Research Foundation - Privacy Policy
            </h2>

            <h1 className=" mt-4 text-gray-800 text-2xl font-bold dark:text-white">
              Arctic Research Foundation Privacy Policy
            </h1>

            <div className=" mt-2 w-12 border-2 bg-blue-600 border-blue-600"></div>

            <h2 className=" mt-4 text-gray-500 text-sm dark:text-gray-400">
              Effective Date (Last Updated): December 2025
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The Arctic Research Foundation ("ARF", "we", "us", "our") is
                committed to protecting the privacy of those who interact with
                us and use our website and web platform ("you", "your").
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                This Privacy Policy ("Policy") applies to personal information
                we collect when you visit, access, or use our website and/or use
                our web platform found at
                <a
                  target="_blank"
                  className="text-blue-600 dark:text-blue-400"
                  href="https://www.arcticfocus.org/">
                  {" "}
                  https://arcticfocus.org
                </a>{" "}
                (the "Site").
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The purpose of this Policy is to explain our privacy practices,
                including:
              </p>
            </blockquote>

            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                The types of personal information we may collect,
              </li>
              <li className="mt-4 ml-24 px-4">
                How we use and disclose your personal information,
              </li>
              <li className="mt-4 ml-24 px-4">Your rights and choices, and</li>
              <li className="mt-4 ml-24 px-4">
                Our practices for maintaining and protecting your information.
              </li>
            </ul>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Please read this Policy carefully. By accessing or using the
                Site, you indicate that you understand, accept and consent to
                the practices described in this Policy
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Personal Information We Collect and How
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Personal information is information about an identifiable
                individual. It includes information such as your name, phone
                number, and email, as well as your options and preferences.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We collect certain types of personal information in the
                following circumstances:
              </p>
            </blockquote>
            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                <span className="font-bold dark:text-gray-300">
                  Account Registration:{" "}
                </span>
                The Site permits you to create a user account (an "Account"). If
                you decide to create an Account, you may be required to provide
                us with certain personal information, such as your name, email
                address, username, affiliation with any university, organization
                or research entity, and password.
              </li>
              <li className="mt-4 ml-24 px-4">
                <span className="font-bold dark:text-gray-300">
                  Newsletter or Event Sign-Up:{" "}
                </span>
                If you subscribe to an ARF newsletter or register for an
                ARF-hosted event, we may collect your name, email address, and
                other contact details.
              </li>
              <li className="mt-4 ml-24 px-4">
                <span className="font-bold dark:text-gray-300">
                  Submitting Content:{" "}
                </span>
                We collect information that you provide directly to us. For
                example, when you submit content such as photographs, videos, or
                text, we receive the information included in your submission,
                which may contain personal details. Content submitted to any
                public area of the Site will be treated as “public”. It will not
                be covered by the privacy protections described in this policy.
                If you are authorized to contribute content or other
                materials—such as data or research findings—you may be asked to
                provide certain personal information, including your name, email
                address, and organizational or research affiliation.
                <p class="mt-2">
                  ARF respects the sovereign rights of Indigenous communities
                  over their data. If any data is identified as having been
                  uploaded in error or without permission by an Indigenous
                  community, it will be removed immediately.
                </p>
                <p class="mt-2">
                  *ARF has implemented measures to safeguard unpublished
                  research data and ensure it is securely stored.
                </p>
              </li>
              <li className="mt-4 ml-24 px-4">
                <span className="font-bold dark:text-gray-300">
                  Communications and Surveys:{" "}
                </span>
                We may collect personal information when you contact us by email
                or participate in optional surveys. This information helps us
                respond to inquiries, enhance our services, and support event
                participation. The Site also automatically collects technical
                data, including IP addresses, browser types, device details, and
                usage patterns. We use this information to manage the Site,
                improve user experience, and analyze trends. Additionally,
                cookies, pixels, and server logs may be used to understand Site
                activity better and optimize its functionality.
              </li>
              <li className="mt-4 ml-24 px-4">
                <span className="font-bold dark:text-gray-300">
                  Passive Collection and Tracking Technologies:{" "}
                </span>
                The Site automatically collects and stores information as you
                use it, including your preferences and anonymous usage
                statistics. This may include Internet Protocol (IP) addresses,
                cookie identifiers, browser type, Internet Service Provider
                (ISP), referring and exit pages, files viewed on our websites
                and applications (e.g., HTML pages, graphics), operating system,
                date and time stamps, and clickstream data. We use this
                information to analyze aggregated trends and manage the Site
                effectively.
                <p class="mt-2">
                  Information collected passively may be combined with other
                  data we hold about you, including personal information, when
                  you are logged into your account.
                </p>
                <p class="mt-2">
                  ARF uses session and persistent cookies, pixels, server logs,
                  and similar technologies to analyze trends, improve and
                  administer the Site, track user interactions, and gather
                  demographic insights. Non-personal data collected through
                  these technologies may also be combined with other information
                  we maintain about you, including personal data, when you are
                  logged into your account.
                </p>
              </li>
              <li className="mt-4 ml-24 px-4">
                <span className="font-bold dark:text-gray-300">
                  Information provided by third parties:{" "}
                </span>
                ARF may collect information about you from third parties or
                supplement the information we collect from you with additional
                records received from third parties. We also collect information
                from third parties to enhance our ability to serve you better
                and to offer you information that may be of interest to you.
              </li>
            </ul>

            <h2 className="mt-4 text-white text-xl font-bold">
              How and Why ARF Uses Your Personal Information
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                ARF collects personal information to operate the Site, provide
                services, and ensure a secure, functional platform. Your
                information may be used to:
              </p>
            </blockquote>

            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                Provide you with our services, including accessing our Site;
              </li>

              <li className="ml-24 px-4">
                Personalize your experience when you use our Site.
              </li>

              <li className="ml-24 px-4">
                Assist us in providing you with a positive and secure
                experience.
              </li>

              <li className="ml-24 px-4">
                Process your subscriptions to any of our newsletters.
              </li>

              <li className="ml-24 px-4">
                Facilitate your requests to participate in any survey or attend
                any events that ARF may organize or make available on the Site.
              </li>

              <li className="ml-24 px-4">
                Use usage data to provide demographic information for research
                purposes for internal use.
              </li>

              <li className="ml-24 px-4">Provide you with support services.</li>

              <li className="ml-24 px-4">
                Improve your experience, support service, and our marketing
                initiatives.
              </li>

              <li className="ml-24 px-4">
                Fulfill the purposes for which you provided the information or
                that were described when it was collected.
              </li>

              <li className="ml-24 px-4">
                Perform analytics, monitor, and analyze trends, usage, and
                activities.
              </li>

              <li className="ml-24 px-4">
                Engage in third-party behavioural advertising and remarketing
                using third-party vendors such as Google.
              </li>

              <li className="ml-24 px-4">
                Manage our business and for any other legitimate business
                interests.
              </li>

              <li className="ml-24 px-4">
                Detect security incidents, protect against malicious, deceptive,
                fraudulent, or illegal activity.
              </li>

              <li className="ml-24 px-4">
                Comply with legal or regulatory requirements; and
              </li>

              <li className="ml-24 px-4">
                For any other purpose with your consent.
              </li>
            </ul>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Sharing Your Personal Information
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We only share your personal information when we have a
                legitimate business reason to do so. We do not rent, sell or
                trade any of your personal information to other parties. We may
                share your information:
              </p>
            </blockquote>

            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                To third parties, including service providers we rely on to host
                the Site. We may disclose your personal information to a
                third-party service provider when necessary to provide the
                service you have requested; however, only the minimal amount of
                information required to perform the service will be disclosed.
                We only permit third parties to process your personal
                information for specified purposes in accordance with this
                Policy, and we require third parties to have appropriate
                safeguards in place to protect your personal information.
              </li>

              <li className="ml-24 px-4">
                In accordance with applicable law, to a buyer or other successor
                in the event of a merger, divestiture, restructuring,
                reorganization, dissolution, or other sale or transfer of some
                or all of our assets, whether as a going concern or as part of
                bankruptcy, liquidation, or similar proceeding, in which
                personal information held by us about users is among the assets
                transferred;
              </li>

              <li className="ml-24 px-4">
                To comply with any court order, law, or legal process, including
                to respond to any government or regulatory request, in
                accordance with applicable law;
              </li>

              <li className="ml-24 px-4">
                If we believe disclosure is necessary to protect our rights,
                property, or safety, or the rights, property or safety of our
                website users, and
              </li>

              <li className="ml-24 px-4">
                In any other way, we may describe when you provide the
                information, or for any other purpose, with your consent.
              </li>
            </ul>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                *The content you post to the Site may be selected and displayed
                by ARF. Other users may be able to see some information about
                you, such as your name, location or profile picture, if you
                choose to upload such information to your Account and/or include
                such information in your posts. ARF is not responsible for how
                other users use information you make public.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Transferring Your Personal Information
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We store personal information that we have collected on secure
                services. We may process, store, and transfer your personal
                information in and to a foreign country with different privacy
                laws that may or may not be as comprehensive as Canadian laws.
                In these circumstances, the governments, courts, law
                enforcement, or regulatory agencies of that country may be able
                to obtain access to your personal information through the laws
                of the foreign country. However, whenever ARF engages a service
                provider, we require that its privacy and security standards
                adhere to this Policy and applicable Canadian privacy
                legislation.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Third-Party Sites
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Our Site may contain links to other websites not controlled by
                us, or other websites not controlled by us may reference or link
                to our Site. Users should read the privacy policies of every
                website they interact with. ARF is not responsible for the
                privacy practices of such other websites, and visiting these
                other websites is at your own risk.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Our Site may contain links and interactive features with various
                social media platforms. If you already use these platforms,
                their cookies may be set on your device when using our Site. You
                should be aware that personal information which you voluntarily
                include and transmit online in a publicly accessible blog, chat
                room, social media platform or otherwise online, or that you
                share in an open forum, may be viewed and used by others without
                any restrictions. We are unable to control such uses of your
                information when interacting with a social media platform. By
                using such services, you assume the risk that the personal
                information provided by you may be viewed and used by third
                parties for any number of purposes.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Use of Cookies
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Our Site uses cookies and similar technologies to enhance
                functionality, improve user experience, and analyze trends.
                These include:
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                <span className="font-bold">Strictly necessary cookies: </span>
                These cookies are essential for the proper functioning of our
                Website. They include, for example, cookies that enable you to
                log into secure areas of our Site. This category of cookies
                cannot be disabled.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                <span className="font-bold">Flash cookies: </span>Certain
                features of our Site may use local stored objects (also known as
                Flash cookies) to collect and store information about your
                preferences and navigation to, from, and on our Site. Flash
                cookies are not managed by the same browser settings that are
                used for browser cookies. To learn how you can manage your Flash
                cookie settings, visit{" "}
                <a
                  href="https://www.adobe.com/ca/products/flashplayer/end-of-life-alternative.html"
                  target="_blank"
                  className="text-blue-600 dark:text-blue-400">
                  this
                </a>{" "}
                page on Adobe's website.

              </p>
            </blockquote>
            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                <span className="font-bold">Analytical cookies: </span>These
                cookies allow us to recognize and count the number of visitors
                and to see how visitors move around our Site when they are using
                it. This helps us improve the way our Site works, for example,
                by ensuring that users can easily find what they are looking
                for. You may refuse to accept browser cookies by activating the
                appropriate setting on your browser. However, if you select this
                setting, you may be unable to access certain parts of our Site.
                Unless you have adjusted your browser settings to refuse cookies,
                our system will issue cookies when you direct your browser to our Site.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Please note that you can disable cookies through your browser
                settings. Although some functionality may be lost, you will
                still be able to access and use the Site. To disable cookies,
                please refer to the instructions for your particular web
                browser.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Your Choices and Rights
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Ensuring your personal information is accurate and current is
                important to us, and we respect the rights you have regarding
                your personal information. You have the right to:
              </p>
            </blockquote>

            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                <span className="font-bold">Access Your Information: </span>
                Request access to your personal information and to obtain a copy
                of the information we hold about you;
              </li>

              <li className="ml-24 px-4">
                <span className="font-bold">Correct Your Information: </span>
                Request corrections if the information we hold is incomplete or
                inaccurate.
              </li>

              <li className="ml-24 px-4">
                <span className="font-bold">Manage Communications: </span>
                Unsubscribe from email communications or newsletters at any time
                using the “unsubscribe” link or by contacting us directly.
              </li>

              <li className="ml-24 px-4">
                <span className="font-bold">Withdraw Consent: </span>
                You may withdraw your consent at any time; however, this will
                not affect the lawfulness of any processing carried out before
                you withdrew your consent. Please note that if you withdraw your
                consent, we may not be able to provide you with all of our
                services. If this is the case, we will notify you at the time
                you withdraw your consent.
              </li>
            </ul>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                If you wish to exercise any of the above rights, please feel
                free to contact us.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                When making a request, we may require you to provide information
                to help us confirm your identity. This is a security measure to
                ensure that your personal information is not disclosed to
                unauthorized individuals. If you are not able to provide proof
                of your identity, we reserve the right to refuse to send you any
                personal information.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We try to respond to all legitimate requests in a reasonable
                timeframe; however, it may take us longer if your request is
                particularly complex or if you have made multiple requests.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                If you choose to receive an ARF newsletter, sign up for a
                recurring alert system, or receive an unwanted email from us,
                you can use the unsubscribe link found at the bottom of the
                email to opt out of receiving future emails. You can also use
                the link provided to check the accuracy of or modify your
                personal information.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We will process your request within a reasonable time after
                receipt. We are not responsible for removing your personal
                information from the lists of any third party who has previously
                been provided with your information in accordance with this
                policy.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Security of Your Personal Information
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                ARF is committed to protecting the privacy of your personal
                information and recognizes the importance of keeping it secure.
                We maintain administrative, technical, and physical safeguards
                to protect your personal information and prevent it from being
                accidentally lost, used, disclosed, altered, or accessed in an
                unauthorized manner.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Unfortunately, the transmission of information via the Internet
                is not completely secure. Although we will do our best to
                protect your personal information, we cannot guarantee its
                security; any personal information provided is at your own risk.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                By using our Site or providing personal information to us, you
                agree that we may communicate with you electronically regarding
                security, privacy, and administrative issues relating to your
                use of our Site. If we learn of a security breach, we may notify
                you by posting a notice on our Site, sending an email to you, or
                by mail, as required by law.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Retention of Personal Information
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We generally retain information for as long as necessary to
                fulfill the purposes for which it was collected. We will also
                retain information as necessary to satisfy any legal,
                accounting, or reporting requirements. ARF maintains a records
                retention policy which has been developed to be consistent with
                industry standards.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Changes to This Policy
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                ARF reviews this Policy annually to ensure it remains current
                and reflects changes to the organization's policy and new
                privacy legislation.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We reserve the right to amend this Policy at any time by posting
                a revised version. The revised version will take effect at the
                time it is posted. We will provide you with reasonable notice of
                any material changes, such as by email notification to our
                contact list and/or by posting a notification on the Site. If
                you continue to use our Site after such changes have been made,
                you hereby provide your consent to the changes. We recommend
                that you review this Policy regularly.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              How to Contact Us
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                If you have any questions about this Policy or the practices
                described herein, please contact our Privacy Officer at:
              </p>

              <p className="mt-4 font-semibold dark:text-gray-400">
                Arctic Research Foundation
                <br />
                67 Erb St W<br />
                Waterloo, Ontario
                <br />
                N2L 6C2
                <br />
              </p>

              <p className="mt-4 font-semibold dark:text-gray-400">
                Email:{" "}
                <a href="mailto:info@arcticresearchfoundation.ca">
                  info@arcticresearchfoundation.ca
                </a>
              </p>
            </blockquote>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default PrivacyPolicy;
